// 这里是定制版病历的打印的页面构建文件,主要的模板变动未来都会在这里变更

import { PrintTemplate } from '../../../../models/print';
import { PrintElement, PageConfig, RenderContext } from '../types/PrintElement';
import { PageManager } from '../manager/PageManager';
import { ElementRenderer } from '../renderer/ElementRenderer';

export interface TreatmentData {
  patientId: string;
  feeCategory: string;
  marital: string;
  setupFlag?: boolean;
  preview?: boolean;
  authorizedName?: string;
  authorizedcode?: string;
  [key: string]: any;
}

export class TreatmentBuilder {
  static buildCommands(data: TreatmentData, _template: PrintTemplate): string {
    console.log("buildCommands...")
    const SECTION_GAP = 2; // 分项与分项之间的段落间距
    const LINE_HEIGHT = 5; // 普通文本内容的单行高度，想让文字更松可优先调这里
    const ROW_SPACING = 2; // 常规流式内容每行后的附加间距
    const ROW_STEP = LINE_HEIGHT + ROW_SPACING; // 处方等固定行布局的行步进
    const HTML_TOP_OFFSET = 1.5; // HTML 内容相对文本基线的上移补偿
    const HTML_HEIGHT = '13mm'; // LODOP HTML 容器实际高度
    const HTML_ESTIMATED_HEIGHT = 6; // HTML 内容用于分页计算的估算高度
    const compatibleMode = data.compatibleMode || false;

    const renderFlowSection = (
      title: string,
      rows: Array<string | { content: string; useHtml?: boolean }>,
      options: { useHtml?: boolean } = {}
    ): void => {
      pageManager.increaseTop(SECTION_GAP);

      renderer.renderElement({
        type: 'text',
        top: pageConfig.currentTop,
        left,
        width: '25mm',
        height: `${LINE_HEIGHT + 1}mm`,
        content: title,
        styles: {
          FontName: fontName,
          Bold: 1
        },
        layoutMode: 'flow',
        spacingAfter: ROW_SPACING
      }, context);

      rows.forEach((row) => {
        const rowContent = typeof row === 'string' ? row : row.content;
        const useHtml = typeof row === 'string' ? Boolean(options.useHtml) : Boolean(row.useHtml ?? options.useHtml);
        const contentElement: PrintElement = useHtml ? {
          type: 'html',
          top: pageConfig.currentTop - HTML_TOP_OFFSET,
          left: left + 19,
          width: '110mm',
          height: HTML_HEIGHT,
          estimatedHeight: HTML_ESTIMATED_HEIGHT,
          content: `<p style='font-size: 12; font-family: "SimSun", SimSun, "宋体", sans-serif;'>${rowContent}</p>`,
          styles: {
            FontName: fontName
          },
          layoutMode: 'flow',
          spacingAfter: ROW_SPACING
        } : {
          type: 'text',
          top: pageConfig.currentTop,
          left: left + 19,
          width: '110mm',
          height: `${LINE_HEIGHT}mm`,
          content: rowContent,
          styles: {
            FontName: fontName
          },
          layoutMode: 'flow',
          spacingAfter: ROW_SPACING
        };

        const contentTop = renderer.renderElement(contentElement, context);

        if (!lineOffConfig) {
          renderer.renderElement({
            type: 'line',
            top: contentTop,
            left: left + 18,
            width: '107mm',
            height: '0mm',
            content: `${contentTop + 3},${left + 18},${contentTop + 3},${left + 125},0,0`,
            layoutMode: 'fixed'
          }, context);
        }
      });
    };

    const renderCompactSection = (
      title: string,
      rows: Array<string | { content: string; useHtml?: boolean }>,
      options: { useHtml?: boolean } = {}
    ): void => {
      pageManager.increaseTop(SECTION_GAP);

      const normalizedRows = rows.map((row) => typeof row === 'string'
        ? { content: row.replace(/[\r\n]/g, ''), useHtml: Boolean(options.useHtml) }
        : { content: row.content.replace(/[\r\n]/g, ''), useHtml: Boolean(row.useHtml ?? options.useHtml) }
      );
      const compactSpacing = 0;

      if (normalizedRows.length === 0) {
        renderer.renderElement({
          type: 'text',
          top: pageConfig.currentTop,
          left,
          width: '25mm',
          height: `${LINE_HEIGHT + 1}mm`,
          content: title,
          styles: {
            FontName: fontName,
            Bold: 1
          },
          layoutMode: 'flow',
          spacingAfter: compactSpacing
        }, context);
        return;
      }

      const firstRowHeight = normalizedRows[0].useHtml ? HTML_ESTIMATED_HEIGHT : LINE_HEIGHT;
      const titleRowTop = pageManager.ensureSpace(firstRowHeight);
      pageManager.setCurrentTop(titleRowTop);

      renderer.renderElement({
        type: 'text',
        top: titleRowTop,
        left,
        width: '25mm',
        height: `${LINE_HEIGHT + 1}mm`,
        content: title,
        styles: {
          FontName: fontName,
          Bold: 1
        },
        layoutMode: 'fixed'
      }, context);

      normalizedRows.forEach((row, index) => {
        const contentElement: PrintElement = row.useHtml ? {
          type: 'html',
          top: pageConfig.currentTop - HTML_TOP_OFFSET,
          left: left + 19,
          width: '110mm',
          height: HTML_HEIGHT,
          estimatedHeight: HTML_ESTIMATED_HEIGHT,
          content: `<p style='font-size: 12; font-family: "SimSun", SimSun, "宋体", sans-serif; margin: 0;'>${row.content}</p>`,
          styles: {
            FontName: fontName
          },
          layoutMode: 'flow',
          spacingAfter: compactSpacing
        } : {
          type: 'text',
          top: pageConfig.currentTop,
          left: left + 19,
          width: '110mm',
          height: `${LINE_HEIGHT}mm`,
          content: row.content,
          styles: {
            FontName: fontName
          },
          layoutMode: 'flow',
          spacingAfter: compactSpacing
        };

        const contentTop = renderer.renderElement(contentElement, context);

        if (!lineOffConfig) {
          renderer.renderElement({
            type: 'line',
            top: contentTop,
            left: left + 18,
            width: '107mm',
            height: '0mm',
            content: `${contentTop + 3.5},${left + 18},${contentTop + 3.5},${left + 125},0,0`,
            layoutMode: 'fixed'
          }, context);
        }

        if (index === 0 && normalizedRows.length > 1 && pageConfig.currentTop < titleRowTop + firstRowHeight) {
          pageManager.setCurrentTop(titleRowTop + firstRowHeight);
        }
      });
    };

    const createDentalContent = (value: string): { content: string; useHtml: boolean } => {

      // 移除换行符
      const cleanValue = value.replace(/[\r\n]/g, '');

      const shouldRenderDentalHtml = data.treatmentRecord?.medicalRecord === 3
        && (cleanValue.includes('#') || !/.*[\u4e00-\u9fa5].*/.test(cleanValue));

      if (!shouldRenderDentalHtml) {
        return { content: cleanValue, useHtml: false };
      }

      const parts = cleanValue.split('#');
      const toothPart = parts[0];
      const detailPart = parts[1] || '';

      if (!toothPart) {
        return { content: detailPart, useHtml: false };
      }

      const teeth = toothPart.split(',');
      let toothSpans = '';
      let prevQuadrant = '';

      for (let i = 0; i < teeth.length; i++) {
        const tooth = teeth[i].trim();
        if (!tooth) continue;

        const currQuadrant = tooth.substring(0, 1);
        if (i > 0 && prevQuadrant !== currQuadrant) {
          toothSpans += ',';
        }
        prevQuadrant = currQuadrant;

        let spanStyle = '';
        if (tooth.startsWith('1')) {
          spanStyle = 'border-right: 1px solid rgba(0, 0, 0, 1); border-bottom: 1px solid rgba(0, 0, 0, 1);';
        } else if (tooth.startsWith('2')) {
          spanStyle = 'border-left: 1px solid rgba(0, 0, 0, 1); border-bottom: 1px solid rgba(0, 0, 0, 1);';
        } else if (tooth.startsWith('3')) {
          spanStyle = 'border-top: 1px solid rgba(0, 0, 0, 1); border-right: 1px solid rgba(0, 0, 0, 1);';
        } else if (tooth.startsWith('4')) {
          spanStyle = 'border-top: 1px solid rgba(0, 0, 0, 1); border-left: 1px solid rgba(0, 0, 0, 1);';
        }

        const toothNumber = tooth.replace(/^[1-4]/, '');
        toothSpans += `&nbsp;<span style='${spanStyle} line-height: 8px; padding: 0.5px; display: inline-block; width: 7px; height: 9px;'>${toothNumber}</span>`;
      }
      return {
        content: detailPart ? `${toothSpans} ${detailPart}` : toothSpans,
        useHtml: true
      };
    };

    const flattenSectionRows = (parentList: any[]): { content: string; useHtml: boolean }[] => {
      const rows: { content: string; useHtml: boolean }[] = [];

      (parentList || []).forEach((group: any) => {
        (group || []).forEach((item: string) => {
          if (!item) return;
          rows.push(createDentalContent(item));
        });
      });

      return rows;
    };

    const {
      topOffset = 0, 
      leftOffset = 0,
      fontConfig = 0,
      paperSizeUnlimitedConfig = false,
      directionConfig = 1,
      titleNameConfig = '',
      titleSuffixConfig = '',
      imgContent = '',
      barCodeConfig = true,
      patientIdConfig = true,
      patientNameSexYearConfig = true,
      feeCategoryConfig = true,
      createTimeConfig = true,
      patientPhoneConfig = true,
      treatmentDepartmentConfig = true,
      patientBirthdayConfig = true,
      maritalStatusConfig = true,
      natyConfig = true,
      companyNameConfig = true,
      jobConfig = true,
      patientAddressConfig = true,
      patientRegionConfig = true,
      chiefComplaintConfig = true,
      historyOfPresentIllnessConfig = true,
      pastMedicalHistoryConfig = true,
      allergenConfig = true,
      personalHistoryConfig = true,
      familyHistoryConfig = true,
      vaccineHistoryConfig = true,
      menstrualHistoryConfig = true,
      maritalHistoryConfig: maritalHistoryConfig2 = true,
      tonguePictureConfig = true,
      pulseConditionConfig = true,
      physicalExaminationConfig = true,
      eyeExaminationConfig = false,
      stomatologyCheckConfig = true,
      inspectionCheckConfig = true,
      printTreatmentConfig = true,
      treatmentPlanConfig = true,
      disposeContentConfig = false,
      diseaseBodyConfig = true,
      printDrugsConfig = true,
      printMaterialsConfig = true,
      onlyHaveDataPrintConfig = true,
      firstLineOff = false,
      secondLineOff = false,
      thirdLineOff = false,
      fourthLineOff = false,
      fifthLineConfig = false,
      lineOffConfig = false,
      printTimeConfig = true,
      patientSignConfig = true,
      authorizedSignConfig = true,
      treatmentConclusionConfig = true
    } = data;
    
    const fontName = fontConfig === 0 ? '黑体' : fontConfig === 1 ? '宋体' : '黑体';
    const left = leftOffset + 5;
    
    const commands: string[] = [];
    
    commands.push(`LODOP.PRINT_INITA(0,0,"148mm","210mm","Treatment");`);
    
    if (paperSizeUnlimitedConfig) {
      if (directionConfig === 1) {
        commands.push(`LODOP.SET_PRINT_PAGESIZE(1,"148mm","210mm","Treatment");`);
      } else {
        commands.push(`LODOP.SET_PRINT_PAGESIZE(2,"210mm","148mm","Treatment");`);
      }
    }
    
    // 添加授权凭证
    commands.push(`LODOP.SET_PRINT_MODE("AUTO_CLOSE_PREWINDOW",1);`);
    commands.push(`LODOP.SET_LICENSES('${data.authorizedName || ''}', '${data.authorizedcode || ''}', '', '');`);
    
    // 初始化页面配置
    const pageConfig: PageConfig = {
      topOffset,
      leftOffset,
      fontName,
      paperSize: directionConfig === 1 ? "148mm×210mm" : "210mm×148mm",
      pageWidth: directionConfig === 1 ? 148 : 210,
      pageHeight: directionConfig === 1 ? 210 : 148,
      currentTop: topOffset + 5,
      itemBeginHeight: 0,
      bodyStartTop: 0,
      bodyEndTop: (directionConfig === 1 ? 210 : 148) - 25,
      footerReserveHeight: 25,
      pageNumber: 1
    };
    
    // 初始化分页管理器和渲染器
    const pageManager = new PageManager(pageConfig, commands);
    const renderer = new ElementRenderer(pageManager);
    const context: RenderContext = {
      data,
      pageConfig,
      commands
    };
    const picElement: PrintElement = {
      type: 'image',
      top: 0,
      left: 0,
      width: '148mm',
      height: '210mm',
      content: imgContent,
      styles: {
        FontName: fontName,
        FontSize: '14',
        Alignment: '2'
      },
      itemType: 1,
      flag: 1,
    };
    // 渲染背景图
    renderer.renderElement(picElement, context);

    // 渲染标题
    const titleElement: PrintElement = {
      type: 'text',
      top: pageConfig.currentTop,
      left: left - 5,
      width: '145mm',
      height: '3mm',
      content: titleNameConfig,
      styles: {
        FontName: fontName,
        FontSize: '14',
        Alignment: '2'
      },
      itemType: 1
    };
    const titleTop = renderer.renderElement(titleElement, context);
    
    // 更新top值
    pageConfig.currentTop = titleTop + 7;
    
    // 渲染条形码
    if (barCodeConfig) {
      const barcodeElement: PrintElement = {
        type: 'barcode',
        top: pageConfig.currentTop,
        left: left,
        width: '115',
        height: '35',
        content: data.patientId,
        styles: {
          FontSize: '9'
        },
        itemType: 1
      };
      const barcodeTop = renderer.renderElement(barcodeElement, context);
      
      // 更新top值
      pageConfig.currentTop = barcodeTop + 1;
    } else {
      // 如果不渲染条形码，也需要更新top值
      pageConfig.currentTop += 1;
    }
    
    // 渲染门诊病历标题
    const medicalRecordTitle: PrintElement = {
      type: 'text',
      top: pageConfig.currentTop,
      left: left - 5,
      width: '145mm',
      height: '5mm',
      content: titleSuffixConfig,
      styles: {
        FontSize: '18',
        Alignment: '2',
        FontName: fontName,
        Bold: 1
      },
      itemType: 1
    };
    const medicalRecordTop = renderer.renderElement(medicalRecordTitle, context);
    
    // 更新top值
    if (!firstLineOff) {
      pageConfig.currentTop = medicalRecordTop + 10;
    } else {
      pageConfig.currentTop = medicalRecordTop;
    }
    
    let moveLeft = 0;
    
    // 渲染患者信息
    if (patientNameSexYearConfig) {
      // 姓名标签
      const nameLabel: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + moveLeft,
        width: '14mm',
        height: '5mm',
        content: '姓名:',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(nameLabel, context);
      
      // 姓名内容
      const nameContent: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop -1,
        left: left + 8 + moveLeft,
        width: '53mm',
        height: '3mm',
        content: data.treatmentRecord?.patientName || '',
        styles: {
          FontName: fontName,
          Bold: 1,
          FontSize: (data.treatmentRecord?.patientName || '').length > 12 ? '8' : '12'
        },
        itemType: 1
      };
      renderer.renderElement(nameContent, context);
      
      // 性别年龄
      const sexAge: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 8 + moveLeft + 43,
        width: '36mm',
        height: '3mm',
        content: `${data.treatmentRecord?.sex || ''} ${data.treatmentRecord?.dripAge || ''}`,
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(sexAge, context);
      
      // 下划线
      const nameLine: PrintElement = {
        type: 'line',
        top: pageConfig.currentTop,
        left: left + 7 + moveLeft,
        width: '59mm',
        height: '0mm',
        content: `${pageConfig.currentTop + 3},${left + 7 + moveLeft},${pageConfig.currentTop + 3},${left + 66 + moveLeft},0,0`,
        itemType: 1
      };
      renderer.renderElement(nameLine, context);
    } else {
      moveLeft -= 67;
    }
    
    // 渲染费别
    if (feeCategoryConfig) {
      const feeLabel: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 67 + moveLeft,
        width: '20mm',
        height: '5mm',
        content: '费别:',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(feeLabel, context);
      
      const feeContent: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 75 + moveLeft,
        width: '200mm',
        height: '3mm',
        content: data.feeCategory || '',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(feeContent, context);
      
      const feeLine: PrintElement = {
        type: 'line',
        top: pageConfig.currentTop,
        left: left + 74 + moveLeft,
        width: '9mm',
        height: '0mm',
        content: `${pageConfig.currentTop + 3},${left + 74 + moveLeft},${pageConfig.currentTop + 3},${left + 83 + moveLeft},0,0`,
        itemType: 1
      };
      renderer.renderElement(feeLine, context);
    } else {
      moveLeft -= 18;
    }
    
    // 渲染就诊日期
    if (createTimeConfig) {
      const dateLabel: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 85 + moveLeft,
        width: '20mm',
        height: '5mm',
        content: '就诊日期:',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(dateLabel, context);
      
      const createTime = data.treatmentRecord?.createTime ? String(data.treatmentRecord.createTime).split(' ')[0].replace(/\//g, '-') : '';
      const dateContent: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 101 + moveLeft,
        width: '200mm',
        height: '3mm',
        content: createTime,
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(dateContent, context);
      
      const dateLine: PrintElement = {
        type: 'line',
        top: pageConfig.currentTop,
        left: left + 100 + moveLeft,
        width: '25mm',
        height: '0mm',
        content: `${pageConfig.currentTop + 3},${left + 100 + moveLeft},${pageConfig.currentTop + 3},${left + 125 + moveLeft},0,0`,
        itemType: 1
      };
      renderer.renderElement(dateLine, context);
    }
    
    // 更新top值
    if (!secondLineOff) {
      pageConfig.currentTop += 5;
    }
    
    moveLeft = 0;
    
    // 渲染病历号
    if (patientIdConfig) {
      const idLabel: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + moveLeft,
        width: '14mm',
        height: '5mm',
        content: '病历号:',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(idLabel, context);
      
      const idContent: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 11 + moveLeft,
        width: '57mm',
        height: '3mm',
        content: data.patientId || '',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(idContent, context);
      
      const idLine: PrintElement = {
        type: 'line',
        top: pageConfig.currentTop,
        left: left + 11 + moveLeft,
        width: '29mm',
        height: '0mm',
        content: `${pageConfig.currentTop + 3},${left + 11 + moveLeft},${pageConfig.currentTop + 3},${left + 40 + moveLeft},0,0`,
        itemType: 1
      };
      renderer.renderElement(idLine, context);
    } else {
      moveLeft -= 41;
    }
    
    // 渲染联系方式
    if (patientPhoneConfig) {
      const phoneLabel: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 41 + moveLeft,
        width: '20mm',
        height: '5mm',
        content: '联系方式:',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(phoneLabel, context);
      
      const phoneContent: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 57 + moveLeft,
        width: '200mm',
        height: '3mm',
        content: data.treatmentRecord?.patientPhone || '',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(phoneContent, context);
      
      const phoneLine: PrintElement = {
        type: 'line',
        top: pageConfig.currentTop,
        left: left + 55 + moveLeft,
        width: '28mm',
        height: '0mm',
        content: `${pageConfig.currentTop + 3},${left + 55 + moveLeft},${pageConfig.currentTop + 3},${left + 83 + moveLeft},0,0`,
        itemType: 1
      };
      renderer.renderElement(phoneLine, context);
    } else {
      moveLeft -= 46;
    }
    
    // 渲染科室
    if (treatmentDepartmentConfig) {
      const deptLabel: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 85 + moveLeft,
        width: '20mm',
        height: '5mm',
        content: '科室:',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(deptLabel, context);
      
      const deptContent: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 95 + moveLeft,
        width: '35mm',
        height: '3mm',
        content: data.treatmentRecord?.treatmentDepartmentName || '',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(deptContent, context);
      
      const deptLine: PrintElement = {
        type: 'line',
        top: pageConfig.currentTop,
        left: left + 94 + moveLeft,
        width: '31mm',
        height: '0mm',
        content: `${pageConfig.currentTop + 3},${left + 94 + moveLeft},${pageConfig.currentTop + 3},${left + 125 + moveLeft},0,0`,
        itemType: 1
      };
      renderer.renderElement(deptLine, context);
    }
    
    // 更新top值
    if (!thirdLineOff) {
      pageConfig.currentTop += 5;
    }
    
    moveLeft = 0;
    
    // 渲染出生年月
    if (patientBirthdayConfig) {
      const birthdayLabel: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + moveLeft,
        width: '20mm',
        height: '5mm',
        content: '出生年月:',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(birthdayLabel, context);
      
      const birthdayContent: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 15 + moveLeft,
        width: '57mm',
        height: '3mm',
        content: data.patient?.birthday || '',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(birthdayContent, context);
      
      const birthdayLine: PrintElement = {
        type: 'line',
        top: pageConfig.currentTop,
        left: left + 14 + moveLeft,
        width: '31mm',
        height: '0mm',
        content: `${pageConfig.currentTop + 3},${left + 14 + moveLeft},${pageConfig.currentTop + 3},${left + 45 + moveLeft},0,0`,
        itemType: 1
      };
      renderer.renderElement(birthdayLine, context);
    } else {
      moveLeft -= 47;
    }
    
    // 渲染婚姻状况
    if (maritalStatusConfig) {
      const maritalLabel: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 47 + moveLeft,
        width: '20mm',
        height: '5mm',
        content: '婚姻:',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(maritalLabel, context);
      
      const maritalContent: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 55 + moveLeft,
        width: '200mm',
        height: '3mm',
        content: data.marital || '',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(maritalContent, context);
      
      const maritalLine: PrintElement = {
        type: 'line',
        top: pageConfig.currentTop,
        left: left + 54 + moveLeft,
        width: '29mm',
        height: '0mm',
        content: `${pageConfig.currentTop + 3},${left + 54 + moveLeft},${pageConfig.currentTop + 3},${left + 83 + moveLeft},0,0`,
        itemType: 1
      };
      renderer.renderElement(maritalLine, context);
    } else {
      moveLeft -= 46;
    }
    
    // 渲染民族
    if (natyConfig) {
      const natyLabel: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 85 + moveLeft,
        width: '20mm',
        height: '5mm',
        content: '民族:',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(natyLabel, context);
      
      const natyContent: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 95 + moveLeft,
        width: '200mm',
        height: '3mm',
        content: data.patient?.nation || '',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(natyContent, context);
      
      const natyLine: PrintElement = {
        type: 'line',
        top: pageConfig.currentTop,
        left: left + 94 + moveLeft,
        width: '31mm',
        height: '0mm',
        content: `${pageConfig.currentTop + 3},${left + 94 + moveLeft},${pageConfig.currentTop + 3},${left + 125 + moveLeft},0,0`,
        itemType: 1
      };
      renderer.renderElement(natyLine, context);
    }
    
    // 更新top值
    if (!fifthLineConfig) {
      pageConfig.currentTop += 5;
    }
    
    moveLeft = 0;
    
    // 渲染工作单位
    if (companyNameConfig) {
      const companyLabel: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + moveLeft,
        width: '20mm',
        height: '5mm',
        content: '工作单位:',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(companyLabel, context);
      
      const companyContent: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 15 + moveLeft,
        width: '57mm',
        height: '3mm',
        content: data.patient?.companyName || '',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(companyContent, context);
      
      const companyLine: PrintElement = {
        type: 'line',
        top: pageConfig.currentTop,
        left: left + 14 + moveLeft,
        width: '69mm',
        height: '0mm',
        content: `${pageConfig.currentTop + 3},${left + 14 + moveLeft},${pageConfig.currentTop + 3},${left + 83 + moveLeft},0,0`,
        itemType: 1
      };
      renderer.renderElement(companyLine, context);
    } else {
      moveLeft -= 85;
    }
    
    // 渲染职业
    if (jobConfig) {
      const jobLabel: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 85 + moveLeft,
        width: '20mm',
        height: '5mm',
        content: '职业:',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(jobLabel, context);
      
      const jobContent: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left + 95 + moveLeft,
        width: '200mm',
        height: '3mm',
        content: data.patient?.job || '',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(jobContent, context);
      
      const jobLine: PrintElement = {
        type: 'line',
        top: pageConfig.currentTop,
        left: left + 94 + moveLeft,
        width: '31mm',
        height: '0mm',
        content: `${pageConfig.currentTop + 3},${left + 94 + moveLeft},${pageConfig.currentTop + 3},${left + 125 + moveLeft},0,0`,
        itemType: 1
      };
      renderer.renderElement(jobLine, context);
    } else {
      moveLeft -= 46;
    }
    
    // 更新top值
    if (!fourthLineOff) {
      pageConfig.currentTop += 5;
    }
    
    moveLeft = 0;
    
    // 渲染地址
    if (patientAddressConfig || patientRegionConfig) {
      const addressLabel: PrintElement = {
        type: 'text',
        top: pageConfig.currentTop,
        left: left,
        width: '25mm',
        height: '5mm',
        content: '地址:',
        styles: {
          FontName: fontName
        },
        itemType: 1
      };
      renderer.renderElement(addressLabel, context);
      
      const addressList = data.addressList || [];
      for (let i = 0; i < addressList.length && i < 2; i++) {
        const addressContent: PrintElement = {
          type: 'text',
          top: pageConfig.currentTop,
          left: left + 8,
          width: '118mm',
          height: '3mm',
          content: addressList[i],
          styles: {
            FontName: fontName
          },
          itemType: 1
        };
        renderer.renderElement(addressContent, context);
        
        const addressLine: PrintElement = {
          type: 'line',
          top: pageConfig.currentTop,
          left: left + 7,
          width: '118mm',
          height: '0mm',
          content: `${pageConfig.currentTop + 3},${left + 7},${pageConfig.currentTop + 3},${left + 125},0,0`,
          itemType: 1
        };
        renderer.renderElement(addressLine, context);
        
        if (i < addressList.length - 1 && i < 1) {
          pageConfig.currentTop += 6;
        }
      }
    }
    
    // 更新top值
    pageConfig.currentTop += 3;
    
    // 渲染分割线（属于页眉固定元素，不参与正文分页）
    const divider: PrintElement = {
      type: 'line',
      top: pageConfig.currentTop,
      left: left,
      width: '130mm',
      height: '0mm',
      content: `${pageConfig.currentTop + 3},${left},${pageConfig.currentTop + 3},${left + 130},3,0`,
      itemType: 1
    };
    const dividerTop = renderer.renderElement(divider, context);
    
    // 设置正文区域起始位置
    pageConfig.itemBeginHeight = dividerTop + 5;
    pageConfig.bodyStartTop = pageConfig.itemBeginHeight;
    pageManager.setBodyBounds(pageConfig.bodyStartTop, pageConfig.pageHeight - (pageConfig.footerReserveHeight || 10));
    pageManager.setCurrentTop(pageConfig.bodyStartTop);
    
    // 渲染主诉
    if (chiefComplaintConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.chiefComplaint)) {
      renderCompactSection('主诉:', data.chiefComplaintList || []);
    }

    // 渲染现病史
    if (historyOfPresentIllnessConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.historyOfPresentIllness)) {
      renderCompactSection('现病史:', data.historyOfPresentIllnessList || []);
    }

    // 渲染既往史
    if (pastMedicalHistoryConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.pastMedicalHistory)) {
      renderCompactSection('既往史:', data.pastMedicalHistoryList || []);
    }

    // 渲染过敏史
    if (allergenConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.allergen)) {
      renderCompactSection('过敏史:', data.allergenList || []);
    }

    // 渲染个人史
    if (personalHistoryConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.personalHistory)) {
      renderCompactSection('个人史:', data.personalHistoryList || []);
    }

    // 渲染家族史
    if (familyHistoryConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.familyHistory)) {
      renderCompactSection('家族史:', data.familyHistoryList || []);
    }

    // 渲染预防接种史
    if (vaccineHistoryConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.vaccineHistory)) {
      renderCompactSection('预防接种史:', data.vaccineHistoryList || []);
    }

    // 渲染月经史
    if (menstrualHistoryConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.menstrualHistory)) {
      renderCompactSection('月经史:', data.menstrualHistoryList || []);
    }

    // 渲染婚姻史
    if (maritalHistoryConfig2 && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.maritalHistory)) {
      renderCompactSection('婚姻史:', data.maritalHistoryList || []);
    }

    // 渲染舌象
    if (tonguePictureConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.tonguePicture)) {
      renderCompactSection('舌象:', data.tonguePictureList || []);
    }

    // 渲染脉象
    if (pulseConditionConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.pulseCondition)) {
      renderCompactSection('脉象:', data.pulseConditionList || []);
    }

    // 渲染体格检查
    if (physicalExaminationConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.physicalExamination)) {
      renderCompactSection('体格检查:', data.physicalExaminationList || []);
    }

    // 渲染眼科检查
    if (eyeExaminationConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.eyeExamination)) {
      renderCompactSection('眼科检查:', data.eyeExaminationList || []);
    }

    // 渲染口腔检查
    if (stomatologyCheckConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.stomatologyCheck)) {
      const stomatologyRows = flattenSectionRows(
        data.stomatologyListParrent || (data.stomatologyCheckList ? data.stomatologyCheckList.map((item: string) => [item]) : [])
      );
      renderCompactSection('口腔检查:', stomatologyRows);
    }

    // 渲染辅助检查
    if (inspectionCheckConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.inspectionCheck)) {
      const inspectionRows = flattenSectionRows(data.inspectionListParrent || []);
      renderCompactSection('辅助检查:', inspectionRows);
    }

    // 渲染诊断
    if (diseaseBodyConfig) {
      const diseaseListParrent = data.diseaseListParrent || [];
      const diagnosisRows = flattenSectionRows(diseaseListParrent);
      const hasDiagnosisData = diagnosisRows.length > 0;

      if (!(onlyHaveDataPrintConfig && !hasDiagnosisData)) {
        renderCompactSection('诊断:', diagnosisRows);
      }
    }

    // 渲染治疗计划
    if (treatmentPlanConfig) {
      const treatmentPlanRows = flattenSectionRows(data.treatmentPlanListParrent || []);
      if (!(onlyHaveDataPrintConfig && treatmentPlanRows.length === 0)) {
        renderCompactSection('治疗计划:', treatmentPlanRows);
      }
    }

    // 渲染治疗意见
    if (treatmentConclusionConfig && !(onlyHaveDataPrintConfig && !data.treatmentRecord?.treatmentConclusion)) {
      const rawConclusion = data.ConclusionListList || [];
      // 后端返回一维字符串数组，需包装为二维再传给 flattenSectionRows
      const conclusionRows = flattenSectionRows(rawConclusion.map((item: string) => [item]));
      renderCompactSection('治疗意见:', conclusionRows);
    }
    
    // 渲染处置
    if (disposeContentConfig) {
      const disposeRows = flattenSectionRows(data.disposeContentListParrent || []);
      if (!(onlyHaveDataPrintConfig && disposeRows.length === 0)) {
        renderCompactSection('处置:', disposeRows);
      }
    }

    // 渲染处方明细
    if (printDrugsConfig || printTreatmentConfig || printMaterialsConfig) {
      pageManager.increaseTop(SECTION_GAP);
      renderer.renderElement({
        type: 'text',
        top: pageConfig.currentTop,
        left,
        width: '140mm',
        height: `${LINE_HEIGHT + 1}mm`,
        content: '处方明细：',
        styles: {
          FontName: fontName,
          Bold: 1
        },
        layoutMode: 'flow',
        spacingAfter: ROW_SPACING
      }, context);

      const dataList = data.dataList || [];
      for (let i = 0; i < dataList.length; i++) {
        const listItem = dataList[i];
        if (!listItem) continue;

        if (printDrugsConfig && listItem.drugItemList) {
          const drugList = listItem.drugItemList;
          let lastGroup = null;

          for (let j = 0; j < drugList.length; j++) {
            const drug = drugList[j];
            if (!drug) continue;

            const rowTop = pageManager.ensureSpace(LINE_HEIGHT);
            pageManager.setCurrentTop(rowTop + ROW_STEP);

            const currentGroup = drug.groupNumber;
            const isFirstInGroup = currentGroup !== lastGroup;

            renderer.renderElement({
              type: 'text',
              top: rowTop,
              left: isFirstInGroup ? left + 3 : left + 8,
              width: '67mm',
              height: `${LINE_HEIGHT}mm`,
              content: isFirstInGroup ? `${currentGroup}. ${drug.drugName || drug.name || ''} ${drug.spec || ''}` : `${drug.drugName || drug.name || ''} ${drug.spec || ''}`,
              styles: {
                FontName: fontName
              },
              layoutMode: 'fixed'
            }, context);

            renderer.renderElement({
              type: 'text',
              top: rowTop,
              left: left + 70,
              width: '46mm',
              height: `${LINE_HEIGHT}mm`,
              content: `${drug.normalUsemethod || ''} ${drug.dosePerTime || ''} ${drug.normalFrequency || ''}`,
              styles: {
                FontName: fontName
              },
              layoutMode: 'fixed'
            }, context);

            renderer.renderElement({
              type: 'text',
              top: rowTop,
              left: left + 115,
              width: '20mm',
              height: `${LINE_HEIGHT}mm`,
              content: drug.useDays !== '-1' && drug.useDays ? `${drug.useDays}天/${drug.drugCount || 0}${drug.unit || drug.accountUnit || ''}` : `${drug.drugCount || 0}${drug.unit || drug.accountUnit || ''}`,
              styles: {
                FontName: fontName
              },
              layoutMode: 'fixed'
            }, context);

            lastGroup = currentGroup;
          }
        }

        // 处方组分隔虚线
        const dividerTop = renderer.renderElement({
          type: 'line',
          top: pageConfig.currentTop,
          left,
          width: '130mm',
          height: '0mm',
          content: `${pageConfig.currentTop},${left},${pageConfig.currentTop},${left + 130},3,0`,
          layoutMode: 'flow',
          estimatedHeight: 3,
          spacingAfter: ROW_SPACING
        }, context);
      }
    }
        
    // 渲染尾部签名和时间（固定在页面底部）
    const pageHeight = directionConfig === 1 ? 210 : 148; // 获取页面高度
    const bottomOffset = 15; // 距纸张底部15mm（上移10mm，防止打印机截断底部内容）
    const fixedTop = pageHeight - bottomOffset - 5 + topOffset; // 计算固定位置的top值，跟随整体偏移

    // 计算启用的项目数量
    const printSettingSignData = data.printSettingSignData || {};
    let enabledCount = 0;
    if (patientSignConfig) enabledCount++;
    if (authorizedSignConfig) enabledCount++;
    if (printSettingSignData.physicianSwitch !== false) enabledCount++;
    if (printTimeConfig) enabledCount++;

    // 根据启用数量计算布局
    const totalWidth = 130;
    let currentIndex = 0;

    if (enabledCount === 4) {
      // 4个项目时保持原有布局
      moveLeft = 0;

      // 渲染患者签名
      if (patientSignConfig) {
        const patientSignElement: PrintElement = {
          type: 'text',
          top: fixedTop,
          left: left - 3,
          width: '50mm',
          height: '5mm',
          content: '患者签名：',
          styles: {
            FontName: fontName
          },
          itemType: 1
        };
        renderer.renderElement(patientSignElement, context);
      }

      // 渲染委托人/监护人签名
      if (authorizedSignConfig) {
        const authorizedSignElement: PrintElement = {
          type: 'text',
          top: fixedTop,
          left: left + 23,
          width: '50mm',
          height: '5mm',
          content: '委托人/监护人签名：',
          styles: {
            FontName: fontName
          },
          itemType: 1
        };
        renderer.renderElement(authorizedSignElement, context);
      }

      // 渲染医生签名
      if (printSettingSignData.physicianSwitch !== false) {
        const doctorLabelElement: PrintElement = {
          type: 'text',
          top: fixedTop,
          left: left + 63,
          width: '20mm',
          height: '5mm',
          content: '医师签名:',
          styles: {
            FontName: fontName
          },
          itemType: 1
        };
        renderer.renderElement(doctorLabelElement, context);

        // 检查是否启用电子签名且有 Base64 图片
        if (printSettingSignData.physicianUseESign && printSettingSignData.physicianBase64) {
          // 使用电子签名图片
          const doctorImageElement: PrintElement = {
            type: 'image',
            top: fixedTop - 5,
            left: left + 78,
            width: '20mm',
            height: '10mm',
            content: printSettingSignData.physicianBase64,
            styles: {
              Stretch: 1
            },
            itemType: 1
          };
          renderer.renderElement(doctorImageElement, context);
        } else {
          // 使用文本签名
          const doctorName = data.treatmentRecord?.treatmentDoctorName || printSettingSignData.physicianName || '';
          const doctorElement: PrintElement = {
            type: 'text',
            top: fixedTop,
            left: left + 80,
            width: '30mm',
            height: '31mm',
            content: doctorName,
            styles: {
              FontName: fontName
            },
            itemType: 1,
            flag: enabledCount
          };
          renderer.renderElement(doctorElement, context);
        }

        // 渲染医生签名下划线
        const doctorLine: PrintElement = {
          type: 'line',
          top: fixedTop + 3,
          left: left + 79,
          width: '20mm',
          height: '0mm',
          content: `${fixedTop + 3},${left + 79},${fixedTop + 3},${left + 96},0,0`,
          itemType: 1
        };
        renderer.renderElement(doctorLine, context);
      }

      // 渲染打印时间
      if (printTimeConfig) {
        const printTime = data.printTime || new Date().toISOString().split('T')[0];
        const timeElement: PrintElement = {
          type: 'text',
          top: fixedTop,
          left: left + 100,
          width: '50mm',
          height: '5mm',
          content: `打印时间：${printTime}`,
          styles: {
            FontName: fontName
          },
          itemType: 1
        };
        renderer.renderElement(timeElement, context);
      }
    } else {
      // 2个或3个项目时等分布局
      const itemWidth = totalWidth / enabledCount;

      // 渲染患者签名
      if (patientSignConfig) {
        const itemLeft = left - 3 + (currentIndex * itemWidth);
        const patientSignElement: PrintElement = {
          type: 'text',
          top: fixedTop,
          left: itemLeft,
          width: '50mm',
          height: '5mm',
          content: '患者签名：',
          styles: {
            FontName: fontName
          },
          itemType: 1
        };
        renderer.renderElement(patientSignElement, context);
        currentIndex++;
      }

      // 渲染委托人/监护人签名
      if (authorizedSignConfig) {
        const itemLeft = left - 3 + (currentIndex * itemWidth);
        const authorizedSignElement: PrintElement = {
          type: 'text',
          top: fixedTop,
          left: itemLeft,
          width: '50mm',
          height: '5mm',
          content: '委托人/监护人签名：',
          styles: {
            FontName: fontName
          },
          itemType: 1
        };
        renderer.renderElement(authorizedSignElement, context);
        currentIndex++;
      }

      // 渲染医生签名
      if (printSettingSignData.physicianSwitch !== false) {
        const itemLeft = left - 3 + (currentIndex * itemWidth);
        const doctorLabelElement: PrintElement = {
          type: 'text',
          top: fixedTop,
          left: itemLeft,
          width: '20mm',
          height: '5mm',
          content: '医师签名:',
          styles: {
            FontName: fontName
          },
          itemType: 1
        };
        renderer.renderElement(doctorLabelElement, context);

        // 检查是否启用电子签名且有 Base64 图片
        if (printSettingSignData.physicianUseESign && printSettingSignData.physicianBase64) {
          // 使用电子签名图片
          const doctorImageElement: PrintElement = {
            type: 'image',
            top: fixedTop - 5,
            left: itemLeft + 15,
            width: '20mm',
            height: '10mm',
            content: printSettingSignData.physicianBase64,
            styles: {
              Stretch: 1
            },
            itemType: 1
          };
          renderer.renderElement(doctorImageElement, context);
        } else {
          // 使用文本签名
          const doctorName = data.treatmentRecord?.treatmentDoctorName || printSettingSignData.physicianName || '';
          const doctorElement: PrintElement = {
            type: 'text',
            top: fixedTop,
            left: itemLeft + 17,
            width: '30mm',
            height: '31mm',
            content: doctorName,
            styles: {
              FontName: fontName
            },
            itemType: 1,
            flag: enabledCount
          };
          renderer.renderElement(doctorElement, context);
        }

        // 渲染医生签名下划线
        const doctorLine: PrintElement = {
          type: 'line',
          top: fixedTop + 3,
          left: itemLeft + 16,
          width: '20mm',
          height: '0mm',
          content: `${fixedTop + 3},${itemLeft + 16},${fixedTop + 3},${itemLeft + 42},0,0`,
          itemType: 1,
          flag: enabledCount
        };
        renderer.renderElement(doctorLine, context);
        currentIndex++;
      }

      // 渲染打印时间
      if (printTimeConfig) {
        const printTime = data.printTime || new Date().toISOString().split('T')[0];
        const timeElement: PrintElement = {
          type: 'text',
          top: fixedTop,
          left: left + 100,
          width: '50mm',
          height: '5mm',
          content: `打印时间：${printTime}`,
          styles: {
            FontName: fontName
          },
          itemType: 1
        };
        renderer.renderElement(timeElement, context);
      }
    }
    
    // 渲染页码（底部水平居中）
    const pageNumberElement: PrintElement = {
      type: 'text',
      top: pageHeight - 15 + topOffset, // 底部15mm位置
      left: left - 5, // 左侧对齐页面
      width: '145mm', // 页面宽度
      height: '5mm',
      content: '第#页/共&页', // 占位符，#表示当前页，&表示总页数
      styles: {
        FontName: fontName,
        Alignment: '2' // 水平居中
      },
      itemType: 2 // 设置为页号项，每页输出
    };
    
    renderer.renderElement(pageNumberElement, context);
    
    // 结束打印或预览
    if (data.preview) {
      commands.push('LODOP.PREVIEW();');
    } else {
      commands.push('LODOP.PRINT();');
    }
    console.log("最终构建内容",commands)
    return commands.join('\n');
  }
}
